import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DotsSixVertical, Trash, Copy, Image, VideoCamera, Check, Clock, Monitor, DeviceMobile } from '@phosphor-icons/react';

export const SortableSceneItem = ({ scene, isSelected, onSelect, onDelete, onDuplicate, onUpdateAspectRatio }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: scene.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto'
  };

  const handleAspectRatioChange = (e, ratio) => {
    e.stopPropagation();
    onUpdateAspectRatio(scene.id, ratio);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group p-3 border-b border-[#262626] cursor-pointer transition-colors ${
        isSelected ? 'bg-[#262626] border-l-2 border-l-[#FF5D01]' : 'hover:bg-[#1a1a1a]'
      }`}
      onClick={() => onSelect(scene)}
      data-testid={`scene-item-${scene.id}`}
    >
      <div className="flex items-start gap-2">
        <button
          {...attributes}
          {...listeners}
          className="mt-1 p-1 text-[#A1A1AA] hover:text-white cursor-grab active:cursor-grabbing"
          data-testid={`drag-handle-${scene.id}`}
        >
          <DotsSixVertical size={16} weight="bold" />
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#FF5D01] font-mono text-xs">#{scene.scene_number}</span>
            {scene.media_type === 'video' ? (
              <VideoCamera size={14} className="text-[#A1A1AA]" />
            ) : (
              <Image size={14} className="text-[#A1A1AA]" />
            )}
            <span className="flex items-center gap-1 text-[#A1A1AA] text-xs">
              <Clock size={12} />
              {scene.duration}s
            </span>
            {/* Per-scene aspect ratio indicator */}
            <span className={`flex items-center text-xs ${scene.aspect_ratio === '9:16' ? 'text-[#2563EB]' : 'text-[#A1A1AA]'}`}>
              {scene.aspect_ratio === '9:16' ? <DeviceMobile size={12} /> : <Monitor size={12} />}
            </span>
            {scene.status === 'approved' && (
              <span className="flex items-center gap-1 text-[#10B981] text-xs">
                <Check size={12} weight="bold" />
              </span>
            )}
          </div>
          <p className="text-sm text-white line-clamp-2 mb-1">{scene.summary}</p>
          
          {/* Aspect ratio toggle for scene */}
          <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => handleAspectRatioChange(e, '16:9')}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] transition-colors ${
                scene.aspect_ratio !== '9:16' ? 'bg-[#FF5D01] text-white' : 'bg-[#333333] text-[#A1A1AA] hover:text-white'
              }`}
              title="16:9 Landscape"
            >
              <Monitor size={10} /> 16:9
            </button>
            <button
              onClick={(e) => handleAspectRatioChange(e, '9:16')}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] transition-colors ${
                scene.aspect_ratio === '9:16' ? 'bg-[#2563EB] text-white' : 'bg-[#333333] text-[#A1A1AA] hover:text-white'
              }`}
              title="9:16 Vertical"
            >
              <DeviceMobile size={10} /> 9:16
            </button>
          </div>
          
          {scene.asset_url && (
            <div className="w-full h-12 rounded overflow-hidden mt-2">
              <img src={scene.asset_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(scene.id); }}
            className="p-1 text-[#A1A1AA] hover:text-white hover:bg-[#333333] rounded"
            title="Duplicate"
            data-testid={`duplicate-scene-${scene.id}`}
          >
            <Copy size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(scene.id); }}
            className="p-1 text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#333333] rounded"
            title="Delete"
            data-testid={`delete-scene-${scene.id}`}
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const SceneItemDragOverlay = ({ scene }) => {
  return (
    <div className="p-3 bg-[#262626] border border-[#FF5D01] rounded-lg shadow-xl">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[#FF5D01] font-mono text-xs">#{scene.scene_number}</span>
        <span className="text-[#A1A1AA] text-xs">{scene.duration}s</span>
      </div>
      <p className="text-sm text-white line-clamp-2">{scene.summary}</p>
    </div>
  );
};

export const SceneList = ({ scenes, selectedScene, onSelectScene, onDeleteScene, onDuplicateScene, onUpdateAspectRatio }) => {
  if (scenes.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <p className="text-[#A1A1AA] text-sm text-center">
          No scenes yet. Upload or paste your script above to generate scenes.
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="py-2">
        {scenes.map((scene) => (
          <SortableSceneItem
            key={scene.id}
            scene={scene}
            isSelected={selectedScene?.id === scene.id}
            onSelect={onSelectScene}
            onDelete={onDeleteScene}
            onDuplicate={onDuplicateScene}
            onUpdateAspectRatio={onUpdateAspectRatio}
          />
        ))}
      </div>
    </ScrollArea>
  );
};
